import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { screeningFlowDraftsApi } from '../../../apis';
import {
  InternalApiCreateScreeningFlowDraft,
  InternalApiEntityType,
  InternalApiScreeningFlowDraft,
} from '../../../api-internal';
import { useMe } from '../../../organisation/useMe';

const organisationDraftsKey = (entityType: InternalApiEntityType, organisationId?: number) => [
  'organisationFlowDrafts',
  entityType,
  organisationId,
];

const flowDraftsKey = (flowId: string | undefined, organisationId?: number) => ['flowDrafts', flowId, organisationId];

const invalidateDrafts = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ['organisationFlowDrafts'] });
  queryClient.invalidateQueries({ queryKey: ['flowDrafts'] });
};

export const useCreateDraftForFlow = (flowId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: InternalApiCreateScreeningFlowDraft) =>
      screeningFlowDraftsApi.createScreeningFlowDraftForFlow({
        flowId,
        internalApiCreateScreeningFlowDraft: body,
      }),
    onSuccess: () => invalidateDrafts(queryClient),
  });
};

export const useCreateNewFlowDraft = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: InternalApiCreateScreeningFlowDraft) =>
      screeningFlowDraftsApi.createNewScreeningFlowDraft({
        internalApiCreateScreeningFlowDraft: body,
      }),
    onSuccess: () => invalidateDrafts(queryClient),
  });
};

export const useDeleteFlowDraft = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (draft: InternalApiScreeningFlowDraft) =>
      screeningFlowDraftsApi.deleteScreeningFlowDraft({ draftId: draft.id }).then(() => draft),
    onSuccess: () => invalidateDrafts(queryClient),
  });
};

export const useActivateFlowDraft = () => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();

  return useMutation({
    mutationFn: (draft: InternalApiScreeningFlowDraft) =>
      screeningFlowDraftsApi.activateScreeningFlowDraft({ draftId: draft.id }).then(() => draft),
    onSuccess: async (draft) => {
      invalidateDrafts(queryClient);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['screeningFlows', draft.entityType, currentOrganisation?.id] }),
        queryClient.invalidateQueries({ queryKey: ['screeningFlow', draft.flowId, currentOrganisation?.id] }),
      ]);
    },
  });
};

export const useUpdateFlowDraft = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ draftId, body }: { draftId: number; body: InternalApiCreateScreeningFlowDraft }) =>
      screeningFlowDraftsApi.updateScreeningFlowDraft({ draftId, internalApiCreateScreeningFlowDraft: body }),
    onSuccess: () => invalidateDrafts(queryClient),
  });
};

export const useQueryFlowDrafts = (flowId: string | undefined, enabled = true) => {
  const { currentOrganisation } = useMe();

  return useQuery({
    queryKey: flowDraftsKey(flowId, currentOrganisation?.id),
    queryFn: () => screeningFlowDraftsApi.findScreeningFlowDrafts({ flowId: flowId! }),
    enabled: enabled && !!flowId && !!currentOrganisation?.id,
  });
};

export const useQueryOrganisationFlowDrafts = (entityType: InternalApiEntityType, enabled = true) => {
  const { currentOrganisation } = useMe();

  return useQuery({
    queryKey: organisationDraftsKey(entityType, currentOrganisation?.id),
    queryFn: () => screeningFlowDraftsApi.findOrganisationScreeningFlowDrafts({ entityType }),
    enabled: enabled && !!currentOrganisation?.id,
  });
};
